import { useTranslation } from 'react-i18next';

// Utils & misc
import cn from '../../../utils/cn';

// Components
import Checkbox from '../../atoms/Checkbox/Checkbox';
import Picto from '../../atoms/Picto/Picto';

// Style
import style from './MenuManagementRow.module.css';

/**
 * MenuManagementRow component - Checkbox (visibility), optional favorite star and expand toggle.
 * @param {object} params
 * @param {string} params.id - Unique id (checkbox id)
 * @param {string} params.label - Item label
 * @param {import('../../../domain/menu/menuManagementUseCase').MenuItemCheckState} params.checkState - Checkbox state
 * @param {(checked: boolean) => void} params.onCheckedChange - Checkbox change handler
 * @param {boolean | null} [params.favorite=null] - Favorite state; `null` hides the star
 * @param {(favorite: boolean) => void} [params.onFavoriteChange] - Star click handler
 * @param {boolean | null} [params.isExpanded=null] - Expand state; `null` hides the toggle
 * @param {() => void} [params.onExpandToggle] - Expand toggle handler
 * @returns {JSX.Element} MenuManagementRow component
 */
function MenuManagementRow({
  id,
  label,
  checkState,
  onCheckedChange,
  favorite = null,
  onFavoriteChange,
  isExpanded = null,
  onExpandToggle,
}) {
  const { t } = useTranslation();
  const isVisible = checkState !== 'unchecked';
  const favoriteLabel = t(favorite ? 'menu.management.removeFromFavorites' : 'menu.management.addToFavorites');
  const expandLabel = t(isExpanded ? 'menu.management.collapse' : 'menu.management.expand');

  return (
    <div className={style.row}>
      <Checkbox
        id={id}
        label={label}
        size="small"
        theme="identitary"
        checked={checkState === 'checked'}
        indeterminate={checkState === 'indeterminate'}
        multiline
        // Indeterminate click checks the whole branch
        onChange={() => onCheckedChange(checkState !== 'checked')}
      />
      {favorite !== null ? (
        <button
          type="button"
          className={cn([style.action, favorite && style.favorite])}
          disabled={!isVisible}
          title={favoriteLabel}
          aria-label={favoriteLabel}
          aria-pressed={favorite}
          onClick={() => onFavoriteChange?.(!favorite)}
        >
          <Picto icon={favorite ? 'star-filled' : 'star'} className={style.star} />
        </button>
      ) : null}
      {isExpanded !== null ? (
        <button
          type="button"
          className={style.action}
          title={expandLabel}
          aria-label={expandLabel}
          aria-expanded={isExpanded}
          onClick={() => onExpandToggle?.()}
        >
          <Picto icon="arrow-down" className={cn([style.chevron, isExpanded && style['chevron-up']])} />
        </button>
      ) : null}
    </div>
  );
}

export default MenuManagementRow;
